import { useEffect, useMemo, useState } from "react";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import Select from "@/components/Select";
import {
  sectionConsultantRoleLabel,
  type CoverageGroupBy,
  type CoverageGroupMember,
  type CoverageGroupRow
} from "../types";

type ArchiveCoverageRolesDialogProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: (member: CoverageGroupMember) => void;
  saving?: boolean;
  groupBy: CoverageGroupBy;
  group: CoverageGroupRow | null;
};

function memberLabel(member: CoverageGroupMember, groupBy: CoverageGroupBy) {
  const name = groupBy === "consultant" ? member.sectionName : (member.person?.name ?? member.sectionName);
  const role = member.roleType ? ` (${sectionConsultantRoleLabel(member.roleType)})` : "";
  return `${name}${role}`;
}

export default function ArchiveCoverageRolesDialog({
  open,
  onClose,
  onConfirm,
  saving = false,
  groupBy,
  group
}: ArchiveCoverageRolesDialogProps) {
  const [selectedAssignmentId, setSelectedAssignmentId] = useState("");
  const archiveableMembers = useMemo(() => group?.members.filter((member) => member.assignmentId) ?? [], [group]);
  const selectedMember = archiveableMembers.find((member) => member.assignmentId === selectedAssignmentId);
  const selectedLabel = selectedMember ? memberLabel(selectedMember, groupBy) : null;
  const archiveHeadline = groupBy === "section" ? "Archive Consultant" : "Archive Section Role";
  const selectLabel = groupBy === "section" ? "Consultant" : "Section Role";

  useEffect(() => {
    if (open) {
      setSelectedAssignmentId("");
    }
  }, [group?.id, open]);

  return (
    <Dialog
      open={open}
      onClosed={onClose}
      headline={archiveHeadline}
      className="w-[560px] max-w-[95vw] max-h-[85vh]"
      hasFormElements
      disableClose={saving}
      actions={
        <>
          <Button variant="text" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="filled"
            theme="error"
            onClick={() => selectedMember && onConfirm(selectedMember)}
            disabled={!selectedMember || saving}
            loading={saving}
            testId="archive-coverage-role-save"
          >
            Archive
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        <Select
          label={selectLabel}
          required
          value={selectedAssignmentId}
          options={archiveableMembers.map((member) => ({
            value: member.assignmentId!,
            label: memberLabel(member, groupBy)
          }))}
          onSelect={setSelectedAssignmentId}
          dropdownPlacement="above"
          data-testid="archive-coverage-role"
        />
        <p>{selectedLabel ? `Archive ${selectedLabel}?` : `Select a ${selectLabel.toLowerCase()} to archive.`}</p>
      </div>
    </Dialog>
  );
}
